/**
 * OrchestrateHQ Brand Theme Configuration
 *
 * Purpose: Centralized Material-UI theme configuration following Orchestrate brand identity
 * Brand Document: docs/product/vision/Orchestrate_Brand_Identity.md
 *
 * Architecture: Defines complete MUI theme with brand colors, typography, and component overrides
 *
 * Dependencies: @mui/material
 * Author: Jeb Wilson
 * Created: 2025-11-17
 * Last Updated: 2025-11-19
 */

import { alpha, createTheme, ThemeOptions } from '@mui/material/styles';

/**
 * Module augmentation to extend MUI's palette with custom colors
 * This enables using 'maestro' as a color prop on MUI components like Button
 */
declare module '@mui/material/styles' {
  interface Palette {
    maestro: Palette['primary'] & MaestroSurfaceColor;
  }
  interface PaletteOptions {
    maestro?: PaletteOptions['primary'] & Partial<MaestroSurfaceColor>;
  }
}

/**
 * The one fill for anything Maestro owns or renders read-only (ORCH-426):
 * the field card, status messages, question cards and the field chips. The
 * design rule is "outline means you can type here; shading means it belongs
 * to Maestro", so these areas take this fill and no border, while the inputs
 * inside them keep their outline. Read it as `maestro.surface` in `sx`.
 */
interface MaestroSurfaceColor {
  surface: string;
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    maestro: true;
  }
}

/**
 * Brand Color Palette
 * Source: Orchestrate Brand Identity Document
 */
const brandColors = {
  // Primary Palette
  deepTeal: '#0A413C', // Core brand color — rich, confident, grounded
  teal: '#126A63', // Supportive accent — modern and tech-forward
  deepViolet: '#551264', // Secondary core — creative, transformative
  lavenderGray: '#9187AE', // Soft neutral with sophistication
  black: '#000000', // Foundational for typography and contrast

  // Neutral Scale
  darkCharcoal: '#222222', // Headings, contrast text
  mediumGray: '#555555', // Subheadlines, UI outlines
  coolGray: '#999999', // Secondary text
  lightGray: '#CCCCCC', // Dividers, backgrounds
  paleGray: '#EEEEEE', // Subtle fills
  offWhite: '#F4F4F4', // Page backgrounds

  // Accent Palette
  mediumTeal: '#306A66', // Balanced accent, blends teal family
  brightTeal: '#43A9A2', // Energetic highlight
  aqua: '#75E0D7', // Vibrant, light accent
  purple: '#712C80', // Rich complementary tone
  softLavender: '#A380CC', // Gentle, humanizing accent
  paleLavender: '#D5CAF3', // Light highlight tone

  // Maestro Accent (for "Chat with Maestro" button and similar CTAs)
  maestro: '#F04684', // Bright magenta/pink - Brand spec
  maestroLight: '#F57DA8', // Lighter variant
  maestroDark: '#C03569', // Darker variant
};

/**
 * Semantic Colors
 * Source: Orchestrate Brand Identity Document
 */
const semanticColors = {
  error: {
    main: '#C61010', // Brand spec - Error main
    light: '#F8C9C9', // Brand spec - Error light
    dark: '#8B0C0C', // Derived darker shade
    contrastText: '#ffffff',
  },
  warning: {
    main: '#EA7907', // Brand spec - Warning main
    light: '#F9CDB2', // Brand spec - Warning light
    dark: '#B85D06', // Derived darker shade
    contrastText: '#ffffff',
  },
  success: {
    main: '#12B740', // Brand spec - Success main
    light: '#B9EAC7', // Brand spec - Success light
    dark: '#0E8F33', // Derived darker shade
    contrastText: '#ffffff',
  },
  info: {
    main: brandColors.brightTeal, // Using brand accent color
    light: brandColors.aqua,
    dark: brandColors.mediumTeal,
    contrastText: '#ffffff',
  },
};

/**
 * Preset Chip Colors for Value Display
 * 12 distinct colors for categorizing and displaying list values in MDM and other components
 * Colors are chosen to work harmoniously with the brand palette while providing clear visual distinction
 */
export const valueChipColors = {
  // Brand colors (4)
  teal: {
    main: brandColors.teal,
    light: brandColors.brightTeal,
    dark: brandColors.deepTeal,
    contrastText: '#ffffff',
  },
  violet: {
    main: brandColors.deepViolet,
    light: brandColors.softLavender,
    dark: brandColors.purple,
    contrastText: '#ffffff',
  },
  lavender: {
    main: brandColors.lavenderGray,
    light: brandColors.paleLavender,
    dark: brandColors.purple,
    contrastText: '#ffffff',
  },
  pink: {
    main: '#E91E63', // Material pink.500 (lighter)
    light: '#F06292', // Material pink.300
    dark: '#C2185B', // Material pink.700
    contrastText: '#ffffff',
  },

  // Complementary colors (8)
  blue: {
    main: '#1976D2', // Material blue.700
    light: '#42A5F5', // Material blue.400
    dark: '#1565C0', // Material blue.800
    contrastText: '#ffffff',
  },
  indigo: {
    main: '#3949AB', // Material indigo.700
    light: '#5C6BC0', // Material indigo.400
    dark: '#303F9F', // Material indigo.800
    contrastText: '#ffffff',
  },
  cyan: {
    main: '#0097A7', // Material cyan.700
    light: '#26C6DA', // Material cyan.400
    dark: '#00838F', // Material cyan.800
    contrastText: '#ffffff',
  },
  green: {
    main: '#66BB6A', // Material green.400 (lighter green, distinct from teal)
    light: '#81C784', // Material green.300
    dark: '#4CAF50', // Material green.500
    contrastText: '#ffffff',
  },
  bile: {
    main: '#827717', // Material lime.800
    light: '#AEEA00', // Material lime.A400
    dark: '#558B2F', // Material light-green.700
    contrastText: '#ffffff',
  },
  orange: {
    main: semanticColors.warning.main,
    light: semanticColors.warning.light,
    dark: semanticColors.warning.dark,
    contrastText: '#ffffff',
  },
  red: {
    main: semanticColors.error.main,
    light: semanticColors.error.light,
    dark: semanticColors.error.dark,
    contrastText: '#ffffff',
  },
  slate: {
    main: '#546E7A', // Material blue-grey.600
    light: '#78909C', // Material blue-grey.400
    dark: '#455A64', // Material blue-grey.700
    contrastText: '#ffffff',
  },
  black: {
    main: '#212121', // Material grey.900 (dark black)
    light: '#424242', // Material grey.800
    dark: '#000000', // Pure black
    contrastText: '#ffffff',
  },
  brown: {
    main: '#5D4037', // Material brown.700
    light: '#8D6E63', // Material brown.400
    dark: '#3E2723', // Material brown.900
    contrastText: '#ffffff',
  },
} as const;

/**
 * Type for value chip color keys
 */
export type ValueChipColorKey = keyof typeof valueChipColors;

/**
 * Array of color keys for easy iteration and indexing
 */
export const valueChipColorKeys: ValueChipColorKey[] = [
  'red',
  'orange',
  'bile',
  'green',
  'cyan',
  'teal',
  'blue',
  'indigo',
  'violet',
  'lavender',
  'pink',
  'brown',
  'slate',
  'black',
];

/**
 * Get a chip color by key
 * @param key - The color key (e.g., 'teal', 'blue', 'green')
 * @returns The color object with main, light, dark, and contrastText
 */
export function getValueChipColor(key: ValueChipColorKey) {
  return valueChipColors[key];
}

/**
 * Get a chip color by index (0-11)
 * Useful for cycling through colors or assigning colors based on position
 * @param index - The index (0-11)
 * @returns The color object
 */
export function getValueChipColorByIndex(index: number) {
  const normalizedIndex = index % valueChipColorKeys.length;
  const key = valueChipColorKeys[normalizedIndex];
  return valueChipColors[key];
}

/**
 * Get a chip color key by index
 * @param index - The index (0-11)
 * @returns The color key string
 */
export function getValueChipColorKeyByIndex(index: number): ValueChipColorKey {
  const normalizedIndex = index % valueChipColorKeys.length;
  return valueChipColorKeys[normalizedIndex];
}

/**
 * Typography Configuration
 * Font System: Poppins SemiBold (all headings h1-h6), Roboto Regular (body)
 * Source: Orchestrate Brand Identity Document
 *
 * Note: Fellix is a commercial font. Using Poppins as fallback until Fellix is available.
 * To add Fellix: Include font files and update index.html
 */
const typography = {
  // Font families with fallback chain
  fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',

  // Headlines: Fellix SemiBold (with Poppins fallback)
  // H1-H2: Brand spec sizes for main titles
  // H3-H6: Practical sizes for application headings (brand spec sizes are for UI micro-labels)
  h1: {
    fontFamily: '"Poppins", "Fellix", Roboto, sans-serif',
    fontWeight: 600, // Brand spec: SemiBold
    fontSize: '2.5rem', // 40px - Brand spec
    lineHeight: 1.1, // 44px / 40px = 1.1
  },
  h2: {
    fontFamily: '"Poppins", "Fellix", Roboto, sans-serif',
    fontWeight: 600, // Brand spec: SemiBold
    fontSize: '1.875rem', // 30px - Brand spec
    lineHeight: 1.133, // 34px / 30px ≈ 1.133
  },

  // Subheadlines: Practical sizes for application use
  h3: {
    fontFamily: '"Poppins", "Fellix", Roboto, sans-serif',
    fontWeight: 600, // Brand spec: SemiBold
    fontSize: '1.5rem', // 24px - Practical for card/portlet headers
    lineHeight: 1.25,
  },
  h4: {
    fontFamily: '"Poppins", "Fellix", Roboto, sans-serif',
    fontWeight: 600, // Brand spec: SemiBold
    fontSize: '1.25rem', // 20px - Practical for subsection headers
    lineHeight: 1.3,
  },
  h5: {
    fontFamily: '"Poppins", "Fellix", Roboto, sans-serif',
    fontWeight: 600, // Brand spec: SemiBold
    fontSize: '1.125rem', // 18px - Practical for small headers
    lineHeight: 1.333,
  },
  h6: {
    fontFamily: '"Poppins", "Fellix", Roboto, sans-serif',
    fontWeight: 600, // Brand spec: SemiBold
    fontSize: '1rem', // 16px - Practical for smallest headers
    lineHeight: 1.375,
  },

  // Body Copy: Roboto Regular
  body1: {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 400,
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  body2: {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 400,
    fontSize: '0.875rem',
    lineHeight: 1.43,
  },

  // Buttons and labels
  button: {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 500,
    fontSize: '0.875rem',
    textTransform: 'none' as const,
  },

  // Captions and overlines
  caption: {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 400,
    fontSize: '0.75rem',
    lineHeight: 1.66,
  },
  overline: {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 400,
    fontSize: '0.75rem',
    lineHeight: 2.66,
    textTransform: 'uppercase' as const,
  },
};

/**
 * Orchestrate Theme Configuration
 * Complete MUI theme following brand identity guidelines
 */
const themeOptions: ThemeOptions = {
  // Spacing system (reduced from default 8px to 6px for compact UI)
  spacing: (factor: number) => `${factor * 5}px`,

  // Color palette
  palette: {
    mode: 'light',

    // Primary: Teal family
    primary: {
      main: brandColors.teal,
      dark: brandColors.deepTeal,
      light: brandColors.brightTeal,
      contrastText: '#ffffff',
    },

    // Secondary: Violet family
    secondary: {
      main: brandColors.deepViolet,
      light: brandColors.softLavender,
      dark: brandColors.purple,
      contrastText: '#ffffff',
    },

    // Maestro: Magenta/Pink for CTAs like "Chat with Maestro"
    maestro: {
      main: brandColors.maestro,
      light: brandColors.maestroLight,
      dark: brandColors.maestroDark,
      contrastText: '#ffffff',
      // Shared Maestro surface (ORCH-426): the brand's "subtle fill" gray.
      surface: brandColors.paleGray,
    },

    // Semantic colors
    error: semanticColors.error,
    warning: semanticColors.warning,
    success: semanticColors.success,
    info: semanticColors.info,

    // Background colors
    background: {
      default: brandColors.offWhite,
      paper: '#ffffff',
    },

    // Text colors
    text: {
      primary: brandColors.darkCharcoal,
      secondary: brandColors.mediumGray,
      disabled: brandColors.coolGray,
    },

    // Divider color
    divider: brandColors.lightGray,

    // Gray scale
    grey: {
      50: brandColors.offWhite,
      100: brandColors.paleGray,
      200: brandColors.lightGray,
      300: brandColors.coolGray,
      400: brandColors.mediumGray,
      500: brandColors.mediumGray,
      600: brandColors.darkCharcoal,
      700: brandColors.darkCharcoal,
      800: brandColors.black,
      900: brandColors.black,
    },
  },

  // Typography
  typography,

  // Shape
  shape: {
    borderRadius: 8,
  },

  // Component overrides
  components: {
    // Global size reduction for form components
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },

    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: brandColors.lightGray,
            },
            '&:hover fieldset': {
              borderColor: brandColors.mediumTeal,
            },
            '&.Mui-focused fieldset': {
              borderColor: brandColors.teal,
            },
          },
        },
      },
    },

    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },

    MuiInputLabel: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          color: brandColors.mediumGray,
          '&.Mui-focused': {
            color: brandColors.teal,
          },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: '4px', // Dense-mode spacing: all form controls default to size='small' above
          fontSize: typography.caption.fontSize,
          lineHeight: typography.caption.lineHeight,
          color: brandColors.coolGray,
        },
      },
    },

    // Button hierarchy (ORCH-424, first applied on ORCH-382). Emphasis follows what we
    // want people to do: `contained` is the one action a screen or dialog is for; `text`
    // is a secondary action (Cancel, Back, Close, Stay) and, with color="error", a
    // tertiary destructive one (Delete, Remove, Deactivate); `outlined` only where two
    // actions are equally valid choices. A text button has no border at rest, so its
    // hover tint and the keyboard focus ring are what keep it discoverable, on touch
    // screens included. Both are set here, once; no footer re-specifies them.
    MuiButton: {
      defaultProps: {
        size: 'small',
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 6,
          padding: '6px 16px',
          '&.Mui-focusVisible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
          variants: [
            {
              // Destructive level (DestructiveButton): a red text button, usually with a
              // start icon. Material icons carry whitespace inside their 24-unit grid (the
              // trash glyph fills the middle half), so MUI's stock margins read as a wide
              // gap and a wider hover shape than Cancel's. The icon's margins are pulled in
              // by that whitespace so the visible icon-to-text gap is 8px and the hover
              // shape hugs the glyph with the same 16px padding as a neutral text button.
              props: { variant: 'text', color: 'error' },
              style: {
                '& .MuiButton-startIcon': {
                  marginLeft: -4,
                  marginRight: 4,
                },
                '& .MuiButton-startIcon > *:nth-of-type(1)': {
                  fontSize: 18,
                },
              },
            },
          ],
        }),
        contained: {
          fontWeight: 700,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          },
        },
        text: ({ theme, ownerState }) => {
          const color = ownerState.color;
          const tone =
            color && color !== 'inherit' ? theme.palette[color].main : theme.palette.text.primary;
          return {
            '&:hover': {
              backgroundColor: alpha(tone, 0.08),
            },
          };
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },

    // Card styling
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          border: `1px solid ${brandColors.paleGray}`,
          borderRadius: 8,
        },
      },
    },

    // Paper styling
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
        elevation2: {
          boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
        },
        elevation3: {
          boxShadow: '0 8px 16px rgba(0,0,0,0.14)',
        },
      },
    },

    // Chip styling
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
        // One rule for every filled chip (ORCH-410): a neutral gray for `color="default"`, and
        // for any palette colour a light tint of that colour with the dark text colour. The
        // brand's filled mains fail WCAG AA with white text (success 2.7:1, warning 2.9:1), so
        // status chips are tinted, not filled, and the label is always readable. StatusChip
        // picks the colour centrally (metadataService.getStatusColor); nothing colours a chip
        // per grid. `orchestrateTheme.test.ts` checks every tint against text.primary at 4.5:1.
        filled: ({ theme, ownerState }) => {
          const color = ownerState.color;
          if (!color || color === 'default') {
            return {
              backgroundColor: brandColors.paleGray,
              color: brandColors.darkCharcoal,
              '&:hover': {
                backgroundColor: brandColors.lightGray,
              },
            };
          }
          const tone = theme.palette[color];
          return {
            backgroundColor: tone.light,
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: alpha(tone.main, 0.45),
            },
          };
        },
        outlined: {
          borderColor: brandColors.lightGray,
          '&:hover': {
            backgroundColor: brandColors.offWhite,
          },
        },
      },
    },

    // Alert styling
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardInfo: {
          backgroundColor: brandColors.paleLavender,
          color: brandColors.darkCharcoal,
        },
      },
    },

    // Table styling
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: brandColors.paleGray,
        },
        head: {
          backgroundColor: brandColors.offWhite,
          color: brandColors.darkCharcoal,
          fontWeight: 700,
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: brandColors.offWhite,
          },
          '&.Mui-selected': {
            backgroundColor: brandColors.paleLavender,
            '&:hover': {
              backgroundColor: brandColors.softLavender,
            },
          },
        },
      },
    },

    // Divider styling
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: brandColors.lightGray,
        },
      },
    },

    // Stack spacing
    MuiStack: {
      defaultProps: {
        spacing: 2,
      },
    },

    // Link styling
    MuiLink: {
      styleOverrides: {
        root: {
          color: brandColors.teal,
          textDecoration: 'none',
          '&:hover': {
            color: brandColors.deepTeal,
            textDecoration: 'underline',
          },
        },
      },
    },

    // Tab styling
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 36,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minHeight: 36,
          paddingTop: 6,
          paddingBottom: 6,
          '&.Mui-selected': {
            color: brandColors.teal,
            fontWeight: 700,
          },
        },
      },
    },

    // Badge styling
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 600,
        },
      },
    },

    // Accordion styling - ultra compact spacing
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          margin: 0,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 24,
          '&.Mui-expanded': {
            minHeight: 24,
          },
        },
        content: {
          margin: 0,
          '&.Mui-expanded': {
            paddingBottom: 10,
            margin: 0,
          },
        },
      },
    },

    // Switch styling - Brand teal when active
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: brandColors.teal,
            '& + .MuiSwitch-track': {
              backgroundColor: brandColors.teal,
              opacity: 0.5,
            },
          },
          '&.Mui-checked.Mui-disabled': {
            color: brandColors.mediumTeal,
          },
        },
        track: {
          backgroundColor: brandColors.coolGray,
        },
      },
    },

    // Checkbox styling - Brand teal when checked. One size for every checkbox in the app
    // (ORCH-418): small (20px icon) is the standard for dense lists, tables and pickers, set
    // here once. No component passes its own `size`; the `orchestrateTheme.test` guard fails
    // on one that does.
    MuiCheckbox: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          color: brandColors.coolGray,
          '&.Mui-checked': {
            color: brandColors.teal,
          },
          '&.Mui-checked.Mui-disabled': {
            color: brandColors.mediumTeal,
          },
          '&:hover': {
            backgroundColor: `${brandColors.teal}14`, // 8% opacity
          },
        },
      },
    },

    // Radio button styling - Brand teal when selected
    MuiRadio: {
      styleOverrides: {
        root: {
          color: brandColors.coolGray,
          '&.Mui-checked': {
            color: brandColors.teal,
          },
          '&.Mui-checked.Mui-disabled': {
            color: brandColors.mediumTeal,
          },
          '&:hover': {
            backgroundColor: `${brandColors.teal}14`, // 8% opacity
          },
        },
      },
    },

    // Avatar styling - Brand teal background for system avatars
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.teal,
          color: '#ffffff',
        },
        colorDefault: {
          backgroundColor: brandColors.teal,
          color: '#ffffff',
        },
      },
    },
  },
};

/**
 * Create and export the Orchestrate theme
 */
export const orchestrateTheme = createTheme(themeOptions);

/**
 * Export brand colors and value chip utilities for direct usage in components when needed
 * (Prefer using theme.palette in components, but this provides access to full palette)
 */
export { brandColors };

/**
 * Type-safe theme access helper
 * Usage: import { theme } from '@/theme/orchestrateTheme';
 */
export default orchestrateTheme;
